// src/lib/lettings.ts — the lettings pipeline and marketing a flat (Task L; prototype §34, §40): shapes, labels, calls.
import { api } from './api';
import type { ClosingDueRow } from './checklists';
import type { OutboxItem } from './comms';
import type { ListMeta } from '../types/api';

/**
 * The four Lettings pages (3 Nov 2026, docs/plans/lettings-ui-simplification.md ask 7) — one address each, gated on the
 * same permission pages as their sidebar items (lib/nav.ts) and routes (App.tsx). `key` is the old ?sub= value, so
 * /app/lettings?sub=<key> redirects to `path` (pages/Lettings.tsx).
 */
export const LETTINGS_PAGES = [
  { key: 'viewings', label: 'Viewings', path: '/app/lettings/viewings', pages: ['viewings', 'leads'] },
  { key: 'applications', label: 'Applications', path: '/app/lettings/applications', pages: ['applications', 'reservations'] },
  { key: 'available', label: 'Available', path: '/app/lettings/available', pages: ['reservations', 'listings'] },
  { key: 'offers', label: 'Offers', path: '/app/lettings/offers', pages: ['offers'] },
] as const;
/** The first Lettings page this user can open (where a Lettings-only member of staff lands), or null. */
export const firstLettingsPath = (canAccessPage: (page: string) => boolean): string | null =>
  LETTINGS_PAGES.find((p) => p.pages.some((k) => canAccessPage(k)))?.path ?? null;
/**
 * A Lettings-only member of staff: works Lettings as Lotus Living (reservations.update) with no dashboard (dashboard.read)
 * — the Letting Accelerator since 3 Nov 2026 (migration 067). Their sidebar is the Lettings group alone and /app lands
 * them on Lettings (pages/Home.tsx). An outside agent has no reservations.update, so keeps their agent dashboard.
 */
export const isLettingsDesk = (u: { allAccess?: boolean } | null | undefined, can: (permission: string) => boolean): boolean =>
  !!u && !u.allAccess && !can('dashboard.read') && can('reservations.update');

export const VIEWING_OUTCOMES = [
  { key: 'LIKED', label: 'Keen', colour: '#2bb87c' },
  { key: 'UNSURE', label: 'Thinking about it', colour: '#d9922e' },
  { key: 'NOT_FOR_THEM', label: 'Not for them', colour: '#d9534f' },
  { key: 'NO_SHOW', label: 'No show', colour: '#6b7f95' },
] as const;
export type ViewingOutcome = (typeof VIEWING_OUTCOMES)[number]['key'];
export const MARKET_COLOURS: Record<string, string> = { DRAFT: '#8fa3b8', PUBLISHED: '#2bb87c', RESERVED: '#d9922e', LET: '#2b8a5c', ARCHIVED: '#6b7f95' };

export interface Match {
  listingId: string; slug: string; headline: string; unitId: string; unitReference: string; propertyName: string; bedrooms: number;
  monthlyMinor: number; currency: string; availableFrom: string | null; status: string; marketStatus: string; why: string[]; notes: string[];
  viewings: Array<{ id: string; scheduledAt: string; status: string; outcome: string | null }>; offer: { id: string; status: string; amountMinor: number } | null;
}
export interface ReadinessItem { key: string; label: string; ok: boolean }
export interface Readiness { items: ReadinessItem[]; ok: number; total: number; done: boolean; missing: string[] }
export type Pair = readonly [string, string];
export interface Marketing {
  listingId: string; slug: string; status: string; marketStatus: string; region: string;
  unit: { id: string; reference: string; name: string | null; status: string; bedrooms: number | null };
  property: { id: string; name: string; city: string | null; postcode: string | null };
  fields: Record<string, any>; readiness: Readiness; portalReady: boolean;
  portals: { reference: string; rightmove: { status: string; sentAt: string | null }; zoopla: { status: string; sentAt: string | null }; note: string };
  counts: { viewings: number; pendingOffers: number };
  can: { update: boolean; publish: boolean };
  options: {
    letTypes: Pair[]; furnished: Pair[]; propertyTypes: Pair[]; outsideSpace: Pair[]; parking: Pair[]; councilTaxBands: string[]; epcBands: string[];
    priceQualifiers: Pair[]; areaUnits: Pair[]; maxFeaturesShown: number;
  };
}

export const lettingsApi = {
  board: (q: ViewingsBoardQuery) => fetchBoard(q),
  matches: async (leadId: string) => (await api.get<{ total: number; rule: string; matches: Match[] }>(`/leads/${leadId}/matches`)).data,
  viewing: async (id: string) => (await api.get<Record<string, any>>(`/viewings/${id}`)).data,
  feedback: (id: string, body: { outcome: ViewingOutcome; feedback?: string | null }) => api.post(`/viewings/${id}/feedback`, body),
  marketing: async (listingId: string) => (await api.get<Marketing>(`/listings/${listingId}/marketing`)).data,
  saveMarketing: (listingId: string, body: Record<string, unknown>) => api.patch<Marketing>(`/listings/${listingId}/marketing`, body),
  payload: async (listingId: string) => (await api.get<Record<string, any>>(`/listings/${listingId}/portal-payload`)).data,
  publish: (listingId: string, on: boolean) => api.post(`/listings/${listingId}/${on ? 'publish' : 'unpublish'}`),
};

// ── Lettings → Viewings (docs/plans/lettings-one-form.md; API GET /lettings/viewings, viewingsBoard.service.ts) ──────
/** A preferred viewing time: a weekday and a one-hour start ("10:00"); older requests carry a part of the day instead. */
export interface ViewingSlot { date: string; time?: string | null; part?: string | null }
export type BoardStatus = 'requests' | 'booked' | 'completed' | 'other';
/** One row of the board: a viewing request (a lead with no viewing on the go) or a viewing. */
export interface ViewingsBoardRow {
  kind: 'request' | 'viewing';
  id: string; leadId: string | null; viewingId: string | null;
  name: string | null; email: string | null; phone: string | null; source: string | null;
  propertyId: string | null; propertyName: string | null; listingId: string | null; listingHeadline: string | null;
  /** Booked for: the request's first preferred slot (else when it came in), or the viewing's time — ISO. */
  at: string;
  /** Booked at: when the request came in or the viewing was made — the When filter and the default order — ISO. */
  submittedAt: string;
  slots: ViewingSlot[];
  /** A request: the lead's status; a viewing: the viewing's (BOOKED, CONFIRMED, COMPLETED, NO_SHOW, CANCELLED). */
  status: string;
  outcome: ViewingOutcome | null;
  agent: { id: string; name: string } | null;
  feedback: string | null;
  leadStatus: string | null;
  createdAt: string;
}
export interface ViewingsBoardExtra { counts: Record<BoardStatus, number>; window: { from: string; to: string } }
export interface ViewingsBoardQuery {
  page?: number; perPage?: number; search?: string; propertyId?: string; agentId?: string; status?: BoardStatus;
  from?: string; to?: string; hourFrom?: string; hourTo?: string;
  /** Booked for (the viewing's time, or what a request asked for): inclusive London days, no bound when left out. */
  atFrom?: string; atTo?: string;
}

/** GET /leads/:id (leads.service serializeLead). */
export interface Lead {
  id: string; firstName: string | null; lastName: string | null; name: string | null; email: string | null; phone: string | null;
  listingId: string | null; propertyId: string | null; propertyName: string | null; listingHeadline: string | null;
  /** The flat the request's listing is for (Convert pre-picks it). */ listingUnitId: string | null;
  /** The first move-in that flat allows (null = any day), and whether that is the current tenant leaving. */
  listingAvailableFrom: string | null; listingUnderNotice: boolean;
  source: string; status: string; notes: string | null; assignedTo: string | null; assignedToName: string | null;
  requirements: { preferredSlots?: ViewingSlot[]; moveIn?: string; householdSize?: number; pets?: { has: boolean; detail?: string }; budgetMinor?: number; consentAt?: string; [k: string]: unknown };
  /** The viewing request's whole form (the application form in mode viewing), or null. */
  application: Record<string, any> | null;
  /** The passport / right to rent document sent with the request (kept on the lead, copied to the application on convert). */
  formDocuments?: Array<{ category: string; name: string }>;
  budgetMaxMinor: number | null; moveInFrom: string | null; pets: boolean | null; lostReason: string | null;
  createdAt: string; updatedAt: string;
}
/** Where a lead came in (leads.source), in words — the Viewings table and the viewing drawer's "Came in by". */
export const LEAD_SOURCE: Record<string, string> = { WEBSITE: 'Website', WEBSITE_FORM: 'Website', LISTING_PAGE: 'Website', APP: 'App', AI_CHAT: 'Assistant', PHONE: 'Phone', WALK_IN: 'Walk-in', REFERRAL: 'Referral', PORTAL: 'Portal' };
export const leadSourceLabel = (source?: string | null) => (source ? LEAD_SOURCE[source] ?? source.toLowerCase() : null);
/** GET /viewings/:id (leads.service serializeViewing). */
export interface Viewing {
  id: string; leadId: string | null; listingId: string; listingHeadline: string | null; propertyName: string | null; leadName: string | null;
  scheduledAt: string; durationMinutes: number; agentId: string | null; agentName: string | null;
  status: 'BOOKED' | 'CONFIRMED' | 'COMPLETED' | 'NO_SHOW' | 'CANCELLED'; feedback: string | null;
  outcome: ViewingOutcome | null; outcomeLabel: string | null; feedbackRecordedAt: string | null; feedbackRecordedByName: string | null;
  feedbackDue: boolean; unitId: string | null; unitReference: string | null; createdAt: string;
}
/** Confirm / reschedule answer: the viewing, plus the message sent with it (or why it could not be written). */
export interface ViewingChange extends Viewing { notification: OutboxItem | null; notifyError: { code: string; message: string } | null }
export type NotifyChannel = 'EMAIL' | 'SMS';
export interface NotifyOption { channel: NotifyChannel; message?: string | null }
/** What a message is about — exactly one. */
export type NotifyAbout = { leadId: string } | { viewingId: string } | { reservationId: string } | { userId: string };
export interface NotifyBody { about: NotifyAbout; channel: NotifyChannel; templateKey?: string | null; subject?: string | null; message?: string | null }
export interface FreeFlat { unitId: string; reference: string; name: string | null; bedrooms: number; unitType: string; advertMinor: number; currency: string }

/**
 * Where "Register for home" goes once a viewing is marked completed (3 Nov 2026 — the one rule for staff and the agent
 * who hosted it; docs/plans/viewing-first-onboarding.md): the Refer drawer on Lettings → Available for the flat they saw,
 * &lead= pre-filling it from their own form. Staff pass no `flats` and the viewing's own flat goes straight through (the
 * drawer says so if it is no longer free); an agent passes their free advertised flats — the flat they saw when it is still
 * one of them, else the development's free flats.
 */
export function applyForTenantLink(
  v: { leadId?: string | null; listingId?: string | null; propertyId?: string | null; unitId?: string | null },
  flats?: { listingId: string; unitId: string }[] | null,
): string | null {
  if (!v.leadId) return null;
  const unitId = flats ? flats.find((x) => x.listingId === v.listingId)?.unitId : v.unitId;
  if (unitId) return `/app/lettings/available?unit=${unitId}&lead=${v.leadId}`;
  return `/app/lettings/available?${v.propertyId ? `propertyId=${v.propertyId}&` : ''}lead=${v.leadId}`;
}

/** The status chip of a board row or a drawer: New request / Booked (CONFIRMED too) / Viewed — to complete / Completed · Keen / No show / Cancelled. */
export function viewingChip(kind: 'request' | 'viewing', status: string, outcome?: string | null, at?: string | null): { label: string; colour: string } {
  if (kind === 'request') {
    // CONTACTED is the old pipeline's step between New and Booked; in the one flow a request nobody has booked is simply new.
    return status === 'NEW' || status === 'CONTACTED' ? { label: 'New request', colour: '#d9922e' }
      : status === 'LOST' ? { label: 'Lost', colour: '#6b7f95' } : ['APPLIED', 'CONVERTED'].includes(status) ? { label: 'Applied', colour: '#2b8a5c' }
      : { label: status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()), colour: '#8fa3b8' };
  }
  const oc = VIEWING_OUTCOMES.find((o) => o.key === outcome);
  if (status === 'NO_SHOW') return { label: 'No show', colour: '#6b7f95' };
  if (status === 'CANCELLED') return { label: 'Cancelled', colour: '#d9534f' };
  if (status === 'COMPLETED') return { label: oc ? `Completed · ${oc.label}` : 'Completed', colour: oc?.colour ?? '#2b8a5c' };
  // No Confirm step since 26 Oct 2026 (later): an older CONFIRMED viewing reads as Booked; past its time it waits to be
  // marked completed (3 Nov 2026: nobody applies for them until it is — was "to convert").
  if (at && new Date(at).getTime() < Date.now()) return { label: 'Viewed — to complete', colour: '#d9922e' };
  return { label: 'Booked', colour: '#5b7fd1' };
}

/** GET /lettings/viewings — one page of the board with the group counts (lettingsApi.board). */
async function fetchBoard(q: ViewingsBoardQuery) {
  const r = await api.get<ViewingsBoardRow[]>('/lettings/viewings', q as Record<string, unknown>);
  return { rows: r.data, meta: r.meta as ListMeta, ...(r.extra as unknown as ViewingsBoardExtra) };
}
export const leadsApi = {
  get: async (id: string) => (await api.get<Lead>(`/leads/${id}`)).data,
  create: async (body: { listingId?: string | null; source?: string; form: unknown; contactOnly?: boolean; documents?: unknown[] }) => (await api.post<Lead>('/leads', body)).data,
  update: async (id: string, body: Record<string, unknown>) => (await api.patch<Lead>(`/leads/${id}`, body)).data,
  remove: (id: string) => api.delete(`/leads/${id}`),
  flats: async (id: string, propertyId?: string) => (await api.get<{ propertyId: string | null; propertyName: string | null; flats: FreeFlat[] }>(`/leads/${id}/flats`, { propertyId: propertyId || undefined })).data,
  /** Make this an application: the lead's own form is used (anything sent is merged over it). Returns the application (a Flat row). */
  convert: async (id: string, body: { unitId: string; form?: unknown }) => (await api.post<{ id: string; unitId: string; unitReference: string; propertyName: string }>(`/leads/${id}/convert`, body)).data,
};
export const viewingsApi = {
  get: async (id: string) => (await api.get<Viewing>(`/viewings/${id}`)).data,
  forLead: async (leadId: string) => (await api.get<Viewing[]>('/viewings', { leadId, order: 'desc', perPage: 50 })).data,
  /** `form` (mode viewing) with no `leadId`: the request is made from it first (the agent portal's Book a viewing). */
  /** `override`: staff booking outside the flat's free slots (otherwise 409 SLOT_UNAVAILABLE). */
  book: async (body: { listingId: string; leadId?: string | null; scheduledAt: string; agentId?: string | null; sendConfirmation?: boolean; form?: unknown; contactOnly?: boolean; documents?: unknown[]; override?: boolean }) => (await api.post<Viewing>('/viewings', body)).data,
  confirm: async (id: string, notify?: NotifyOption | null) => (await api.post<ViewingChange>(`/viewings/${id}/confirm`, { notify: notify ?? null })).data,
  reschedule: async (id: string, scheduledAt: string, notify?: NotifyOption | null, override?: boolean) => (await api.post<ViewingChange>(`/viewings/${id}/reschedule`, { scheduledAt, notify: notify ?? null, ...(override ? { override: true } : {}) })).data,
  /** Assign host: who shows them round (null = nobody yet). */
  assign: async (id: string, agentId: string | null) => (await api.patch<Viewing>(`/viewings/${id}`, { agentId })).data,
  /** The reason goes on the viewing's notes (it shows under "What they said"). */
  cancel: async (id: string, reason?: string | null) => (await api.patch<Viewing>(`/viewings/${id}`, { status: 'CANCELLED', ...(reason ? { feedback: reason } : {}) })).data,
  feedback: async (id: string, body: { outcome: ViewingOutcome; feedback?: string | null }) => (await api.post<Viewing>(`/viewings/${id}/feedback`, body)).data,
};
/** POST /lettings/notify — one email or text through the outbox; the outbox item comes back (SENT, QUEUED, DRAFT or FAILED). */
export const notifyApi = {
  send: async (body: NotifyBody) => (await api.post<OutboxItem>('/lettings/notify', body)).data,
};

export const gbp = (minor: number | null | undefined, currency = 'GBP') =>
  minor == null ? '—' : new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: minor % 100 ? 2 : 0 }).format(minor / 100);
export const shortDate = (iso: string | null | undefined) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: iso.length === 10 ? 'UTC' : undefined }) : '—');

// ── What was the Lettings page's Overview, API only since 2026-10-22 (GET /lettings/overview; src/modules/leasing/lettingsOverview.service.ts) ──
// A block the user cannot read comes back null (rent: tenancies, onMarket: listings, moving + closing: checklists,
// changeovers, sheet: reservations — omitted from each row).
export interface DevelopmentLettings {
  propertyId: string; name: string; region: string; city: string | null;
  flats: number; let: number; available: number; reserved: number; builtFlats: number;
  onMarket: number | null; movingIn: number | null; changeovers: number | null;
  occupancyPct: number | null; rentPcmMinor: number | null; currency: string;
  sheet?: { id: string; status: string } | null;
}
export interface AvailableFlat {
  unitId: string; reference: string; unitType: string; bedrooms: number; propertyId: string; propertyName: string;
  rentPcmMinor: number; currency: string; advertised: boolean; listingId: string | null;
}
export interface MovingRow {
  checklistId: string; tenancyId: string; unitId: string; unitReference: string; tenantName: string;
  propertyId: string; propertyName: string; date: string | null;
  done: number; total: number; percent: number; status: string; welcomeDue: boolean; overdue: boolean;
}
export interface LettingsOverview {
  visible: { rent: boolean; listings: boolean; moving: boolean; changeovers: boolean; reservations: boolean };
  stats: {
    flats: number; let: number; available: number; reserved: number;
    movingIn: number | null; onMarket: number | null; changeovers: number | null; occupancyPct: number | null;
    rentRollPa: Array<{ currency: string; minor: number }> | null;
  };
  developments: DevelopmentLettings[];
  availableNow: AvailableFlat[];
  closingDue: ClosingDueRow[] | null;
  movingIn: MovingRow[] | null;
  movingOut: MovingRow[] | null;
}
export const lettingsOverview = async () => (await api.get<LettingsOverview>('/lettings/overview')).data;
